import { useQuery, useMutation, useQueryClient, queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './query-keys';
import type {
  AnalyticsFilters,
  SaveWidgetPreferencesRequest,
  SaveWidgetPreferencesResponse,
  WidgetPreferences,
} from '@/types/analytics';

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for global analytics
 * Also fetches article analytics and merges them into the response
 */
export const globalAnalyticsQueryOptions = (filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.global(filters),
    queryFn: async () => {
      // Fetch both global analytics and article analytics in parallel
      const [globalResponse, articleResponse] = await Promise.all([
        api.getGlobalAnalytics({ filters }),
        api.getArticleAnalytics().catch(() => null), // Don't fail if articles not available
      ]);

      // Merge article analytics into the response
      return {
        ...globalResponse,
        articleAnalytics: articleResponse ?? undefined,
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for quizlet analytics
 */
export const quizletAnalyticsQueryOptions = (quizletId: string, filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.quizlet(quizletId, filters),
    queryFn: async () => {
      const response = await api.getQuizletAnalytics({ quizletId, filters });
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for Daily Knowledge analytics
 */
export const dailyKnowledgeAnalyticsQueryOptions = (filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.dailyKnowledge(filters),
    queryFn: async () => {
      const response = await api.getDailyKnowledgeAnalytics({ filters });
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for question analytics
 */
export const questionAnalyticsQueryOptions = (questionId: string) =>
  queryOptions({
    queryKey: queryKeys.analytics.question(questionId),
    queryFn: async () => {
      const response = await api.getQuestionAnalytics(questionId);
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for streak data
 */
export const streakDataQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.analytics.streak(),
    queryFn: async () => {
      const response = await api.getStreakData();
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for insights and recommendations
 */
export const insightsQueryOptions = (filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.insights(filters),
    queryFn: async () => {
      const response = await api.getInsights({ filters });
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for widget preferences
 */
export const widgetPreferencesQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.analytics.preferences(),
    queryFn: async () => {
      const response = await api.getWidgetPreferences();
      return response.preferences;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes (preferences change less frequently)
  });

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch global analytics across all quizlets
 *
 * @param filters - Time range and other filter options
 * @returns Global analytics data including performance metrics, breakdowns, and top quizlets
 */
export function useGlobalAnalytics(filters: AnalyticsFilters) {
  return useQuery(globalAnalyticsQueryOptions(filters));
}

/**
 * Hook to fetch analytics for a specific quizlet
 *
 * @param quizletId - The quizlet ID to fetch analytics for
 * @param filters - Time range and other filter options
 * @returns Quizlet-specific analytics including question-level performance
 */
export function useQuizletAnalytics(quizletId: string, filters: AnalyticsFilters) {
  return useQuery(quizletAnalyticsQueryOptions(quizletId, filters));
}

/**
 * Hook to fetch Daily Knowledge analytics and activity data
 *
 * @param filters - Time range and other filter options
 * @returns Daily Knowledge streak, performance metrics, and activity heatmap
 */
export function useDailyKnowledgeAnalytics(filters: AnalyticsFilters) {
  return useQuery(dailyKnowledgeAnalyticsQueryOptions(filters));
}

/**
 * Hook to fetch analytics for a specific question
 *
 * @param questionId - The question ID to fetch analytics for
 * @returns Question performance, common wrong answers, and attempt history
 */
export function useQuestionAnalytics(questionId: string) {
  return useQuery(questionAnalyticsQueryOptions(questionId));
}

/**
 * Hook to fetch Daily Knowledge streak data and milestones
 *
 * @returns Current streak, longest streak, and achievement milestones
 */
export function useStreakData() {
  return useQuery(streakDataQueryOptions());
}

/**
 * Hook to fetch automated insights and practice recommendations
 *
 * @param filters - Time range and other filter options
 * @returns AI-generated insights, recommendations, and best study times
 */
export function useInsights(filters: AnalyticsFilters) {
  return useQuery(insightsQueryOptions(filters));
}

/**
 * Hook to fetch user's widget preferences for both global and quizlet dashboards
 *
 * @returns Array of widget preferences (one per dashboard type)
 */
export function useWidgetPreferences() {
  return useQuery(widgetPreferencesQueryOptions());
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to save user's widget preferences
 *
 * Updates which widgets are enabled, their order, and widget-specific settings.
 * Automatically invalidates the preferences cache to trigger a refetch.
 */
export function useSaveWidgetPreferences() {
  const queryClient = useQueryClient();

  return useMutation<
    SaveWidgetPreferencesResponse,
    Error,
    SaveWidgetPreferencesRequest,
    { previous?: WidgetPreferences[] }
  >({
    mutationFn: (request: SaveWidgetPreferencesRequest) =>
      api.saveWidgetPreferences(request),

    // Optimistic update for instant UI feedback
    onMutate: async (newPreferences) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.analytics.preferences(),
      });

      // Snapshot previous value
      const previous = queryClient.getQueryData<WidgetPreferences[]>(
        queryKeys.analytics.preferences()
      );

      // Optimistically update
      if (previous) {
        const updated = previous.map((pref) =>
          pref.dashboardType === newPreferences.dashboardType
            ? {
              ...pref,
              enabledWidgets: newPreferences.enabledWidgets,
              widgetOrder: newPreferences.widgetOrder,
              widgetSettings: newPreferences.widgetSettings,
              updatedAt: new Date().toISOString(),
            }
            : pref
        );
        queryClient.setQueryData<WidgetPreferences[]>(
          queryKeys.analytics.preferences(),
          updated
        );
      }

      return { previous };
    },

    // Rollback on error
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.analytics.preferences(),
          context.previous
        );
      }
    },

    // Refetch to ensure consistency
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.analytics.preferences(),
      });
    },
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to invalidate all analytics queries
 *
 * Useful when session data changes (e.g., after completing a quiz)
 * to ensure analytics are up-to-date.
 */
export function useInvalidateAnalytics() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.analytics.all,
    });
  };
}
